// src/redux/slices/cartSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import apiService from '../../api/authApi';

// ==================== ASYNC THUNKS ====================

// ✅ POST - Add to Cart
export const addToCartAsync = createAsyncThunk(
  'cart/addToCart',
  async (cartData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const userId = state.auth?.user?.id || localStorage.getItem('userId') || 1;
      
      const payload = {
        user_id: parseInt(userId),
        items: Array.isArray(cartData.items) ? cartData.items : [{
          item_id: parseInt(cartData.product_id) || parseInt(cartData.item_id),
          item_type: cartData.item_type || 'product',
          quantity: parseInt(cartData.quantity) || 1
        }]
      };
      
      console.log('🔄 POST - Adding to cart:', payload);
      const response = await apiService.addToCart(payload);
      console.log('✅ POST - Added to cart:', response);
      
      await dispatch(fetchCartItemsAsync());
      
      return response;
    } catch (error) {
      console.error('❌ POST - Add to cart error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to add to cart');
    }
  }
);

// ✅ GET - Fetch cart items
export const fetchCartItemsAsync = createAsyncThunk(
  'cart/fetchCartItems',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 GET - Fetching cart items from API...');
      const response = await apiService.getCartItems();
      console.log('✅ GET - Cart items fetched:', response);
      
      const cartData = response?.data || response?.cartItems || [];
      const formattedItems = Array.isArray(cartData) ? cartData.map(item => ({
        cart_id: parseInt(item.cart_id) || item.id,
        item_id: parseInt(item.item_id) || item.id,
        item_type: item.item_type || 'product',
        quantity: parseInt(item.quantity) || 1,
        price: parseFloat(item.price) || 0,
        subtotal: parseFloat(item.subtotal) || parseFloat(item.price) * parseInt(item.quantity) || 0,
        product_name: item.product_name || item.name || 'Product',
        prod_image: item.prod_image || item.image || null,
        in_thali: item.in_thali || null,
        type: item.type || item.item_type || 'Veg'
      })) : [];
      
      console.log('✅ Formatted cart items:', formattedItems);
      
      localStorage.setItem('cartItems', JSON.stringify(formattedItems));
      
      return { data: formattedItems };
    } catch (error) {
      console.error('❌ GET - Fetch cart error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to fetch cart');
    }
  }
);

// ✅ POST - Update Cart
export const updateCartAsync = createAsyncThunk(
  'cart/updateCart',
  async (updateData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const userId = state.auth?.user?.id || localStorage.getItem('userId') || 1;
      
      let items = [];
      if (Array.isArray(updateData.items)) {
        items = updateData.items.map(item => ({
          item_id: parseInt(item.item_id) || parseInt(item.id),
          quantity: parseInt(item.quantity) || 1
        }));
      } else {
        const itemId = parseInt(updateData.item_id) || parseInt(updateData.product_id) || parseInt(updateData);
        items = [{
          item_id: itemId,
          quantity: parseInt(updateData.quantity) || 1
        }];
      }
      
      const payload = {
        user_id: parseInt(userId),
        items: items
      };
      
      console.log('🔄 POST - Updating cart with payload:', JSON.stringify(payload, null, 2));
      const response = await apiService.updateCart(payload);
      console.log('✅ POST - Cart updated response:', response);
      
      await dispatch(fetchCartItemsAsync());
      
      return response;
    } catch (error) {
      console.error('❌ POST - Update cart error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to update cart');
    }
  }
);

// ✅ DELETE - Remove from cart
export const removeFromCartAsync = createAsyncThunk(
  'cart/removeFromCart',
  async (removeData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const userId = state.auth?.user?.id || localStorage.getItem('userId') || 1;
      
      const cartId = parseInt(removeData.cart_id) || parseInt(removeData.cartId) || parseInt(removeData);
      
      const payload = {
        user_id: parseInt(userId),
        cart_id: cartId
      };
      
      console.log('🔄 DELETE - Removing from cart:', payload);
      const response = await apiService.removeFromCart(payload);
      console.log('✅ DELETE - Removed from cart:', response);
      
      await dispatch(fetchCartItemsAsync());
      
      return response;
    } catch (error) {
      console.error('❌ DELETE - Remove from cart error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to remove from cart');
    }
  }
);

// ✅ POST - Checkout
export const checkoutCartAsync = createAsyncThunk(
  'cart/checkoutCart',
  async (checkoutData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userId = state.auth?.user?.id || localStorage.getItem('userId') || 1;
      
      const payload = {
        user_id: parseInt(userId),
        ...checkoutData
      };
      
      console.log('🔄 POST - Checking out cart:', payload);
      const response = await apiService.checkoutCart(payload);
      console.log('✅ POST - Checkout successful:', response);
      return response;
    } catch (error) {
      console.error('❌ POST - Checkout error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Checkout failed');
    }
  }
);

// ✅ POST - Place Order
export const placeOrderAsync = createAsyncThunk(
  'cart/placeOrder',
  async (orderData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userId = state.auth?.user?.id || localStorage.getItem('userId') || 1;
      
      let cartItems = orderData.items || [];
      console.log('📦 Items from orderData:', cartItems);
      
      if (!cartItems || cartItems.length === 0) {
        cartItems = state.cart?.items || [];
        console.log('📦 Got cart items from Redux state:', cartItems);
      }
      
      if (!cartItems || cartItems.length === 0) {
        try {
          const stored = localStorage.getItem('cartItems');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              cartItems = parsed;
              console.log('📦 Got cart items from localStorage:', cartItems);
            }
          }
        } catch (e) {
          console.warn('Could not parse cart items from localStorage');
        }
      }
      
      if (!cartItems || cartItems.length === 0) {
        try {
          const stored = sessionStorage.getItem('cartItems');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              cartItems = parsed;
              console.log('📦 Got cart items from sessionStorage:', cartItems);
            }
          }
        } catch (e) {
          console.warn('Could not parse cart items from sessionStorage');
        }
      }
      
      if (!cartItems || cartItems.length === 0) {
        throw new Error('Your cart is empty. Please add items before placing an order.');
      }
      
      console.log('📦 Final cart items for order:', cartItems);
      
      const formattedItems = cartItems.map(item => ({
        item_id: parseInt(item.item_id) || parseInt(item.id) || 0,
        quantity: parseInt(item.quantity) || 1,
        price: parseFloat(item.price) || 0,
        item_type: item.item_type || 'product'
      }));
      
      const subtotal = formattedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const gst = subtotal * 0.18;
      const total = subtotal + gst;
      
      const payload = {
        user_id: parseInt(userId),
        payment_method: orderData.payment_method || 'cash',
        customer_address: orderData.customer_address || orderData.address || '',
        customer_phone: orderData.customer_phone || orderData.phone || '',
        customer_name: orderData.customer_name || orderData.name || '',
        items: formattedItems,
        subtotal: subtotal,
        gst: gst,
        total: total,
        delivery_charge: orderData.delivery_charge || 0,
        latitude: orderData.latitude || null,
        longitude: orderData.longitude || null
      };
      
      console.log('🔄 POST - Placing order with items:', formattedItems.length);
      console.log('🔄 POST - Full payload:', JSON.stringify(payload, null, 2));
      
      const response = await apiService.placeOrder(payload);
      console.log('✅ POST - Order placed:', response);
      
      localStorage.removeItem('cartItems');
      sessionStorage.removeItem('cartItems');
      
      return response;
    } catch (error) {
      console.error('❌ POST - Place order error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to place order');
    }
  }
);

// ✅ GET - Order History
export const getOrderHistoryAsync = createAsyncThunk(
  'cart/getOrderHistory',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 GET - Fetching order history...');
      const response = await apiService.getOrderHistory();
      console.log('✅ GET - Order history fetched:', response);
      return response;
    } catch (error) {
      console.error('❌ GET - Order history error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to fetch order history');
    }
  }
);

// ✅ POST - Cancel Order (NEW)
export const cancelOrderAsync = createAsyncThunk(
  'cart/cancelOrder',
  async (orderId, { rejectWithValue, getState, dispatch }) => {
    try {
      console.log('🔄 Cancelling order:', orderId);
      const response = await apiService.cancelOrder(orderId);
      console.log('✅ Order cancelled:', response);
      
      // ✅ Refresh order history after cancellation
      await dispatch(getOrderHistoryAsync());
      
      return response;
    } catch (error) {
      console.error('❌ Cancel order error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to cancel order');
    }
  }
);

// ==================== INITIAL STATE ====================

const initialState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
  isLoading: false,
  error: null,
  lastUpdated: null,
  checkoutStatus: null,
  orderId: null,
  orderNumber: null,
  orderData: null,
  orderHistory: [],
};

// ==================== SLICE ====================

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCheckoutStatus: (state) => {
      state.checkoutStatus = null;
      state.orderId = null;
      state.orderNumber = null;
      state.orderData = null;
    },
    resetCart: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.totalPrice = 0;
      state.error = null;
      state.checkoutStatus = null;
      state.orderId = null;
      state.orderNumber = null;
      state.orderData = null;
      localStorage.removeItem('cartItems');
      sessionStorage.removeItem('cartItems');
    },
    addItemLocally: (state, action) => {
      const { item_id, item_type, quantity, price, product_name, prod_image } = action.payload;
      const existingItem = state.items.find(
        item => String(item.item_id) === String(item_id) && item.item_type === item_type
      );
      if (existingItem) {
        existingItem.quantity = parseInt(existingItem.quantity) + parseInt(quantity || 1);
      } else {
        state.items.push({
          cart_id: Date.now(),
          item_id: String(item_id),
          item_type: item_type || 'product',
          quantity: parseInt(quantity || 1),
          price: parseFloat(price || 0),
          product_name: product_name || 'Product',
          prod_image: prod_image || null
        });
      }
      state.totalItems = state.items.reduce((sum, item) => sum + parseInt(item.quantity), 0);
      state.totalPrice = state.items.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity)), 0);
      localStorage.setItem('cartItems', JSON.stringify(state.items));
    },
    removeItemLocally: (state, action) => {
      const id = String(action.payload);
      state.items = state.items.filter(
        item => String(item.cart_id) !== id && String(item.item_id) !== id
      );
      state.totalItems = state.items.reduce((sum, item) => sum + parseInt(item.quantity), 0);
      state.totalPrice = state.items.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity)), 0);
      localStorage.setItem('cartItems', JSON.stringify(state.items));
    },
    updateQuantityLocally: (state, action) => {
      const { item_id, quantity } = action.payload;
      const id = String(item_id);
      const item = state.items.find(
        item => String(item.item_id) === id || String(item.cart_id) === id
      );
      if (item) {
        item.quantity = parseInt(quantity);
        state.totalItems = state.items.reduce((sum, item) => sum + parseInt(item.quantity), 0);
        state.totalPrice = state.items.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity)), 0);
        localStorage.setItem('cartItems', JSON.stringify(state.items));
      }
    },
    syncCartFromStorage: (state) => {
      try {
        const stored = localStorage.getItem('cartItems');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            state.items = parsed;
            state.totalItems = state.items.reduce((sum, item) => sum + parseInt(item.quantity), 0);
            state.totalPrice = state.items.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity)), 0);
            console.log('✅ Synced cart from localStorage:', state.items);
          }
        }
      } catch (e) {
        console.warn('Could not sync cart from localStorage');
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToCartAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addToCartAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        console.log('✅ Add to cart success');
      })
      .addCase(addToCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to add to cart';
      })
      
      .addCase(fetchCartItemsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCartItemsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const cartData = action.payload?.data || [];
        state.items = Array.isArray(cartData) ? cartData : [];
        state.totalItems = state.items.reduce((sum, item) => sum + parseInt(item.quantity || 1), 0);
        state.totalPrice = state.items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 1)), 0);
        state.lastUpdated = new Date().toISOString();
        state.error = null;
        localStorage.setItem('cartItems', JSON.stringify(state.items));
        console.log('✅ Cart items loaded:', state.items);
        console.log('✅ Total items:', state.totalItems);
        console.log('✅ Total price:', state.totalPrice);
      })
      .addCase(fetchCartItemsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to fetch cart';
      })
      
      .addCase(updateCartAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCartAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        console.log('✅ Cart updated successfully');
      })
      .addCase(updateCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to update cart';
      })
      
      .addCase(removeFromCartAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeFromCartAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        console.log('✅ Item removed from cart');
      })
      .addCase(removeFromCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to remove from cart';
      })
      
      .addCase(checkoutCartAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.checkoutStatus = null;
      })
      .addCase(checkoutCartAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.checkoutStatus = 'success';
        const orderData = action.payload?.data || action.payload;
        state.orderId = orderData?.order_id || null;
        state.orderNumber = orderData?.order_number || null;
        state.orderData = orderData || null;
        state.items = [];
        state.totalItems = 0;
        state.totalPrice = 0;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
        localStorage.removeItem('cartItems');
        console.log('✅ Checkout successful:', action.payload);
      })
      .addCase(checkoutCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.checkoutStatus = 'failed';
        state.error = typeof action.payload === 'string' ? action.payload : 'Checkout failed';
      })
      
      .addCase(placeOrderAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.checkoutStatus = null;
      })
      .addCase(placeOrderAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.checkoutStatus = 'success';
        const orderData = action.payload?.data || action.payload;
        state.orderId = orderData?.order_id || null;
        state.orderNumber = orderData?.order_number || null;
        state.orderData = orderData || null;
        state.items = [];
        state.totalItems = 0;
        state.totalPrice = 0;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
        localStorage.removeItem('cartItems');
        sessionStorage.removeItem('cartItems');
        console.log('✅ Order placed successfully:', action.payload);
      })
      .addCase(placeOrderAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.checkoutStatus = 'failed';
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to place order';
      })
      
      .addCase(getOrderHistoryAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrderHistoryAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orderHistory = action.payload?.data || action.payload || [];
        state.error = null;
        console.log('✅ Order history loaded:', state.orderHistory.length);
      })
      .addCase(getOrderHistoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to fetch order history';
      })
      
      // ✅ Cancel Order Reducers (NEW)
      .addCase(cancelOrderAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelOrderAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        console.log('✅ Order cancelled successfully:', action.payload);
      })
      .addCase(cancelOrderAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to cancel order';
        console.error('❌ Cancel order failed:', state.error);
      });
  },
});

// ==================== EXPORT ACTIONS & REDUCER ====================

export const {
  clearError,
  clearCheckoutStatus,
  resetCart,
  addItemLocally,
  removeItemLocally,
  updateQuantityLocally,
  syncCartFromStorage,
} = cartSlice.actions;

export default cartSlice.reducer;